import {
  ServicesService,
  ZonesService,
  type PostServiceRequestAdmin,
  type PostRegionZonesAdminRequest,
  type PutServiceRequestAdmin
} from '@/backend/market'
import { ServiceRepository } from '../../domain/repositories/ServiceRepository'
import type { ServiceFilterEntity } from '../../domain/entities/ServiceFilterEntity'
import { ServiceFilterModel } from '../models/ServiceFilterModel'
import { ServiceResponseModel } from '../models/ServiceResponseModel'
import type { ServiceResponseEntity } from '../../domain/entities/ServiceResponseEntity'
import { ServiceMiniEntity } from '../../domain/entities/ServiceMiniEntity'
import { ServiceMiniModel } from '../models/ServiceMiniModel'
import type { CreateUpdateServiceEntity } from '../../domain/entities/CreateUpdateServiceEntity'
import { CreateUpdateServiceModel } from '../models/CreateUpdateServiceModel'
import { RegionZonesEntity } from '../../domain/entities/RegionZonesEntity'
import type { ZoneEntity } from '../../domain/entities/ZoneEntity'
import { VehicleTemplateType } from '@/features/vehicleTemplate/domain/enums/VehicleTemplateType'

export class ServiceRepositoryImpl implements ServiceRepository {
  private roundCoordinate(value: number) {
    return Number(value.toFixed(7))
  }

  private isSamePoint(
    first?: { latitude: number; longitude: number },
    second?: { latitude: number; longitude: number }
  ) {
    if (!first || !second) return false
    return first.latitude === second.latitude && first.longitude === second.longitude
  }

  private toApiBoundary(boundary: Array<[number, number]>) {
    const mappedBoundary = boundary.map(([latitude, longitude]) => ({
      latitude: this.roundCoordinate(latitude),
      longitude: this.roundCoordinate(longitude)
    }))
    if (!mappedBoundary.length) return mappedBoundary

    const firstPoint = mappedBoundary[0]
    const lastPoint = mappedBoundary[mappedBoundary.length - 1]

    if (!this.isSamePoint(firstPoint, lastPoint)) {
      mappedBoundary.push(firstPoint)
    }

    return mappedBoundary
  }

  private toLatLngBoundary(boundary?: Array<{ latitude?: number; longitude?: number }> | null) {
    return (
      boundary
        ?.filter(
          (point) => typeof point?.latitude === 'number' && typeof point?.longitude === 'number'
        )
        .map((point) => [point.latitude!, point.longitude!] as [number, number]) ?? []
    )
  }

  private toZoneEntity(zone: {
    id?: number
    boundary?: Array<{ latitude?: number; longitude?: number }> | null
    preferredVehicleTypes?: Array<VehicleTemplateType> | null
    isActive?: boolean
  }): ZoneEntity {
    return {
      id: zone.id,
      boundary: this.toLatLngBoundary(zone.boundary),
      preferredVehicleTypes: zone.preferredVehicleTypes ?? [],
      isActive: zone.isActive ?? true
    }
  }

  async getServices(
    filterData: ServiceFilterEntity
  ): Promise<ServiceResponseEntity | { error: true }> {
    const serviceFilterModel = new ServiceFilterModel(filterData)
    try {
      const response = await ServicesService.getServicesAdmin(serviceFilterModel)
      const serviceResponseModel = new ServiceResponseModel(response)
      return serviceResponseModel
    } catch {
      return { error: true }
    }
  }
  async getServiceById(id: number): Promise<ServiceMiniEntity | { error: true }> {
    try {
      const response = await ServicesService.getServiceAdmin({
        id
      })
      const serviceMiniModel = new ServiceMiniModel(response)

      return serviceMiniModel
    } catch {
      return { error: true }
    }
  }

  async createService(
    data: CreateUpdateServiceEntity
  ): Promise<{ success: true } | { error: true }> {
    const createUpdateServiceModel = new CreateUpdateServiceModel(data)
    try {
      await ServicesService.postServiceAdmin({
        requestBody: createUpdateServiceModel as unknown as PostServiceRequestAdmin
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async updateService(
    id: number,
    data: CreateUpdateServiceEntity
  ): Promise<{ success: true } | { error: true }> {
    const createUpdateServiceModel = new CreateUpdateServiceModel(data)
    try {
      await ServicesService.putServiceAdmin({
        id,
        requestBody: createUpdateServiceModel as unknown as PutServiceRequestAdmin
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async deleteService(id: number): Promise<{ success: true } | { error: true }> {
    try {
      await ServicesService.deleteServiceAdmin({
        id
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async changeServiceStatus(id: number): Promise<{ success: true } | { error: true }> {
    try {
      await ServicesService.changeServiceStatusAdmin({
        id
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async getZones(regionId: number): Promise<RegionZonesEntity | { error: true }> {
    try {
      const response = await ZonesService.getZonesByRegionAdmin({
        regionId
      })
      const result = new RegionZonesEntity()
      result.regionId = regionId
      result.zones = response.zones?.map((zone) => this.toZoneEntity(zone)) ?? []
      return result
    } catch {
      return { error: true }
    }
  }

  async updateZones(data: RegionZonesEntity): Promise<{ success: true } | { error: true }> {
    try {
      const requestBody: PostRegionZonesAdminRequest = {
        regionId: data.regionId,
        zones: data.zones.map((zone) => ({
          id: zone.id,
          boundary: this.toApiBoundary(zone.boundary),
          preferredVehicleTypes: zone.preferredVehicleTypes ?? [],
          isActive: zone.isActive ?? true
        }))
      }
      await ZonesService.postZonesByRegionAdmin({
        requestBody
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async deleteZone(id: number): Promise<{ success: true } | { error: true }> {
    try {
      await ZonesService.deleteZoneAdmin({
        id
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
}
